import { $t } from '@primeuix/themes';
import {
	CUSTOM_PALETTES,
	DEFAULT_CUSTOM_PRIMARY_HEX,
	DEFAULT_CUSTOM_SURFACE_HEX,
	type PrimaryColorName,
	type SurfaceColorName,
	THEME_PRESET,
	generatePaletteFromHex,
} from 'src/theme/themeConfig';

import { setAppCssVar } from 'src/util/css';

const THEME_OPTIONS = {
	cssLayer: false,
	darkModeSelector: '.body--dark',
};

function getCssVar(name: string): string {
	if (typeof document === 'undefined') {
		return '';
	}
	return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

function darkenHex(hex: string, factor: number): string {
	const cleaned = hex.replace('#', '');
	if (cleaned.length !== 6) {
		return hex;
	}

	const r = Math.round(parseInt(cleaned.substring(0, 2), 16) * (1 - factor));
	const g = Math.round(parseInt(cleaned.substring(2, 4), 16) * (1 - factor));
	const b = Math.round(parseInt(cleaned.substring(4, 6), 16) * (1 - factor));

	return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

function resolvePreset(raw: unknown): Record<string, unknown> {
	const obj = raw as Record<string, unknown>;
	if (obj.primitive) {
		return obj;
	}
	if (obj.default && (obj.default as Record<string, unknown>).primitive) {
		return obj.default as Record<string, unknown>;
	}
	return obj;
}

export function applyTheme(
	primary: PrimaryColorName,
	surface: SurfaceColorName,
	customPrimaryHex?: string,
	customSurfaceHex?: string
): void {
	const basePreset = resolvePreset(THEME_PRESET);

	const primitives = basePreset.primitive as Record<string, Record<string, string>> | undefined;
	const primaryPalette =
		primary === 'custom'
			? generatePaletteFromHex(customPrimaryHex ?? DEFAULT_CUSTOM_PRIMARY_HEX)
			: (primitives?.[primary] ?? CUSTOM_PALETTES[primary]);
	const surfacePalette =
		surface === 'custom'
			? generatePaletteFromHex(customSurfaceHex ?? DEFAULT_CUSTOM_SURFACE_HEX)
			: primitives?.[surface];

	const builder = $t({
		options: THEME_OPTIONS,
		preset: basePreset,
	});

	if (primaryPalette) {
		builder.primaryPalette(primaryPalette);
	}

	if (surfacePalette) {
		builder.surfacePalette({ '0': '#ffffff', ...surfacePalette });
	}

	builder.update({ mergeOptions: false, mergePresets: false });

	if (typeof requestAnimationFrame !== 'undefined') {
		requestAnimationFrame(() => bridgeAppCssVars(primaryPalette));
	}
}

function bridgeAppCssVars(primaryPalette?: Record<string, string>): void {
	const primaryHex = primaryPalette?.['500'] ?? getCssVar('--p-primary-500') ?? '#3b82f6';
	setAppCssVar('primary', primaryHex);
	setAppCssVar('secondary', darkenHex(primaryHex, 0.3));
	setAppCssVar('uni-color', primaryHex);

	const surfaceDark = getCssVar('--p-surface-900');
	if (surfaceDark) {
		setAppCssVar('dark', surfaceDark);
		setAppCssVar('dark-page', surfaceDark);
	}
}
